import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import NumberFormat from 'react-number-format';

import { numberFormat } from 'constants/formats';

import ShareRanges from 'modules/_shared/components/ShareRanges';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import AvailableRangesView from 'modules/_shared/components/AvailableRangesView';
import HistoryMovementsView from 'modules/_shared/components/HistoryMovementsView';
import { AvailableRangesViewProvider } from 'modules/_shared/components/AvailableRangesView/context/AvailableRangesViewContext';
import { HistoryMovementsViewProvider } from 'modules/_shared/components/HistoryMovementsView/context/HistoryMovementsViewContext';

import { useSharesTransferModal } from '../context/SharesTransferModalContext';

import SharesTransferModalPartnersSection from './SharesTransferModalPartnersSection';
import PriceView from './PriceView';

const SharesTransferMovementsView: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useSharesTransferModal();

  const { modalLabels } = data;

  const validateRangeDisabledCondition =
    !data.selectedPartnerSellerOption || !data.selectedHoldingClassOption;

  const sharesToTransfer =
    data.shareTo < data.shareFrom || !data.shareTo
      ? 0
      : data.shareTo - data.shareFrom + 1;

  return (
    <>
      <div>
        <SharesTransferModalPartnersSection />

        <hr />

        <div className="form-group">
          <label className="form-label" htmlFor="class-select-input">
            {t('ClassOfParticipations')} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="class-select-input"
              value={data.selectedHoldingClassOption}
              onChange={actions.handleChangeHoldingClass}
              onFetchData={actions.handleFetchHoldingClassByTerm}
              placeholder={t('SearchClass')}
              minimumInputLength={1}
              noDataLabelKey="NoClassesHaveBeenFound"
              loadingLabelKey="LoadingClasses"
              errorLabelKey="ErrorLoadingClasses"
              defaultOptions={data.holdingClassOptions}
            />
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <label className="form-label mb-0" htmlFor="pay-amount">
              {t(modalLabels.transmittingSharesNumber)}
              <span className="badge-primary rounded px-2 py-1 ml-2">
                <NumberFormat
                  displayType="text"
                  value={sharesToTransfer}
                  thousandSeparator={numberFormat.thousandSeparator}
                  decimalSeparator={numberFormat.decimalSeparator}
                />
              </span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <ShareRanges
            sharesRanges={data.validatedRanges}
            shareFrom={data.shareFrom}
            shareTo={data.shareTo}
            isValidRange={data.isValidRange}
            isValidatingRange={data.isValidatingRange}
            additionalConditions={validateRangeDisabledCondition}
            handleChangeShareFrom={actions.handleChangeShareFrom}
            handleChangeShareTo={actions.handleChangeShareTo}
            handleValidateRange={actions.handleValidateRange}
          />
        </div>

        <hr />
        <div className="form-group mb-0">
          <AvailableRangesViewProvider
            availableRanges={data.availableRanges}
            page={data.rangesPage}
            rowsPerPage={data.rangesRowsPerPage}
            totalAvailableRanges={data.totalAvailableRanges}
            isLoadingRanges={data.isLoadingAvailableRanges}
            handleChangePage={actions.handleChangeRangesPage}
            handleChangeRowsPerPage={actions.handleChangeRangesRowsPerPage}
            handleClickRange={actions.handleClickRange}
          >
            <AvailableRangesView />
          </AvailableRangesViewProvider>
        </div>
        <hr />

        <PriceView />
      </div>

      <div className="mt-4">
        <HistoryMovementsViewProvider
          historyMovements={data.historyMovements}
          operationType={data.operationType}
        >
          <HistoryMovementsView />
        </HistoryMovementsViewProvider>
      </div>
    </>
  );
};

export default SharesTransferMovementsView;
